import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/shared/models/user.model';
import { SocketService } from './socket.service';
import { UserJourneyService } from './user-journey.service';
import { OnboardingService } from './on-boarding.service';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';
/**
 * Service for user
 */
@Injectable({ providedIn: 'root' })
/**
 * UserService
 * @class UserService
 */
export class UserService {
  /**
   * inject service
   * @param http HttpClient
   */
  private http = inject(HttpClient);
  /**
   * inject service
   * @param socketService
   */
  private socketService = inject(SocketService);
  /**
 * inject service
 * @param userJourneyService
 */
  private userJourneyService = inject(UserJourneyService);
  /**
* inject service
* @param onBoardingService
*/
  private onBoardingService = inject(OnboardingService);
  /**
* inject service
* @param private storageService = inject(StorageService);
*/
  private storageService = inject(StorageService);
  /**
   * inject dependency
   * @param {Router} router
   */
  protected router = inject(Router);
  /**
   * baseApiUrl
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'manager';
  /**
   * currentUser$
   * @type {BehaviorSubject<User>}
   */
  private currentUser$: BehaviorSubject<User> = new BehaviorSubject(null);
  /**
   * forceLogoutUser$
   * @type {Subject<any>}
   */
  private forceLogoutUser$: Subject<any> = new Subject();
  /**
   * echoInstance
   * @type {any}
   */
  private echoInstance = null;
  /**
   * ProgressBar$
   * @type {Subject<ILoadingBulk>}
   */
  private ProgressBar$: Subject<ILoadingBulk> = new Subject();
  /**
   * pregressNotification$
   * @type {BehaviorSubject<progressNotification>}
   */
  private pregressNotification$: BehaviorSubject<progressNotification> = new BehaviorSubject(null);

  /**
   * Starts listening to socket for user events
   * Subscribe to force logout event and listen to events:
   * - CurrentUserUpdatedEvent: update current user
   * - UserProgressBarEvent: update progress bar for user import
   * - PreloadUrlProgressBarEvent: update progress bar for preload url
   * - PreloadPasswordProgressBarEvent: update progress bar for preload password
   * - DeletePreloadPasswordsProgressBarEvent: update progress bar for delete preload password
   * - DeletePreloadUrlsProgressBarEvent: update progress bar for delete preload url
   * - DeleteUsersProgressBarEvent: update progress bar for delete users
   * - CreditCardProgressBarEvent: update progress bar for credit card
   * - DeleteCreditCardsProgressBarEvent: update progress bar for delete credit card
   * - AccessKitProgressBarEvent: update progress bar for access kit
   * - DeleteAccessKitProgressBarEvent: update progress bar for delete access kit
   * - CreateApplicationAccessPasswordProgressBarEvent: update progress bar for create application access password
   * - CreateApplicationAccessUserProgressBarEvent: update progress bar for create application access user
   * - DeleteApplicationAccessUserProgressBarEvent: update progress bar for delete application access user
   * - DeviceRestrictionProgressBarEvent: update progress bar for device restriction
   * - UpdateImagePreloadPasswordsProgressBarEvent: update progress bar for update preload password images
   * - UpdateImageAccessKitsProgressBarEvent: update progress bar for update access kit images
   * - UpdateImagePreloadUrlsProgressBarEvent: update progress bar for update preload url images
   * - ImportAdUserProgressBarEvent: update progress bar for import ad user
   */
  startSocket(): void {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.user.${this.socketService.userId}`)
      .listen('.CurrentUserUpdatedEvent', (event) => {
        if (!event.user.role.permission.accessConsole) {
          this.forceLogoutUser$.next(null);
        } else {
          if (event.user.showUserJourney) {
            if (this.currentUser.role.permission.manageUsers || this.currentUser.role.permission.managePreloadPasswords) {
              if (this.currentUser.role.level !== event.user.role.level || this.currentUser.role.permission.manageUsers !== event.user.role.permission.manageUsers ||
                this.currentUser.role.permission.managePreloadPasswords !== event.user.role.permission.managePreloadPasswords) {
                this.userJourneyService.getUserJourney().then(response => {
                  this.userJourneyService.setJourneySteps(response.data);
                })
                this.onBoardingService.destroyPopover();
              }
            }
          }
          this.setCurrentUser(new User(event.user));
        }
      })
      .listen('.UserProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.user, value: event.percentage });
      })
      .listen('.PreloadUrlProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.preloadUrl, value: event.percentage });
      })
      .listen('.PreloadPasswordProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.preloadPassword, value: event.percentage });
      })
      .listen('.DeletePreloadPasswordsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deletePreloadPassword, value: event.percentage });
      })
      .listen('.DeletePreloadUrlsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deletePreloadUrl, value: event.percentage });
      })
      .listen('.DeleteUsersProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deleteUsers, value: event.percentage });
      })
      .listen('.CreditCardProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.creditCard, value: event.percentage });
      })
      .listen('.DeleteCreditCardsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deleteCreditCard, value: event.percentage });
      })
      .listen('.AccessKitProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.applicationAccess, value: event.percentage });
      })
      .listen('.DeleteAccessKitProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deleteApplicationAccess, value: event.percentage });
      })
      .listen('.CreateApplicationAccessPasswordProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.uploadUserAccessPassword, value: event.percentage });
      })
      .listen('.CreateApplicationAccessUserProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.uploadUserAccessTemplate, value: event.percentage });
      })
      .listen('.DeleteApplicationAccessUserProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.deletePreloadedUserAcceess, value: event.percentage });
      })
      .listen('.DeviceRestrictionProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.uploadDeviceRestriction, value: event.percentage });
      })
      .listen('.UpdateImagePreloadPasswordsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.updatePreloadPasswordImages, value: event.percentage });
      })
      .listen('.UpdateImageAccessKitsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.updateAKImages, value: event.percentage });
      })
      .listen('.UpdateImagePreloadUrlsProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.updatePreloadUrlImages, value: event.percentage });
      })
      .listen('.ImportAdUserProgressBarEvent', (event) => {
        this.setProgressBulk({ key: blukLoadingOptions.importAzureOrAd, value: event.percentage });
      });
  }

  /**
   * Leaves the private channel for the user's progress notifications.
   * This should be called when the user is no longer interested in receiving progress notifications.
   */
  stopSocket(): void {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.user.${this.socketService.userId}`);
    }
  }

  /**
   * Initializes the progress notification listener for the current user.
   * Establishes a private socket connection to listen for '.ProgressNotificationEvent'.
   * When the event occurs, it updates the current progress notification with the received data.
   */
  startProgressNotification() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.user.${this.socketService.userId}`)
      .listen('.ProgressNotificationEvent', (event) => {
        this.setCurrentProgressNotif(event.data);
      });
  }

  /**
   * Stops listening to the progress notification socket for the current user.
   * Removes the '.ProgressNotificationEvent' listener from the private channel.
   */
  stopProgressNotification(): void {
    if (this.echoInstance) {
      this.echoInstance.private(`company.${this.socketService.companyId}.user.${this.socketService.userId}`)
        .stopListening('.ProgressNotificationEvent');
    }
  }

  /**
   * Sets the current progress notification.
   * @param {progressNotification} progressNotif - The progress notification object to set as the current progress notification.
   */
  setCurrentProgressNotif(progressNotif: progressNotification): void {
    this.pregressNotification$.next(progressNotif);
  }

  SubCurrentProgressNotif(): Observable<progressNotification> {
    return this.pregressNotification$;
  }
  /**
   * Sets the current user.
   * @param {User} user - The user object to set as the current user.
   */
  setCurrentUser(user: User): void {
    this.currentUser$.next(user);
  }

  /**
   * Subscribe to the current user
   * @returns {Observable<User>} Returns an observable of the current user
   */
  SubCurrentUser(): Observable<User> {
    return this.currentUser$;
  }


  get currentUser() {
    return this.currentUser$.getValue();
  }

  /**
   * Update the progress bar.
   * @param {ILoadingBulk} percentage
   * @returns {void}
   */
  setProgressBulk(percentage: ILoadingBulk) {
    this.ProgressBar$.next(percentage);
  }
  /**
   * subscribe to the progress bar
   * @returns {Observable<ILoadingBulk>}
   */
  subProgressBulk(): Observable<ILoadingBulk> {
    return this.ProgressBar$;
  }
  /**
   * call the server to get the user
   * @returns {Promise<any>} returns the current user
   */
  getCurrentUser(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl));
  }

  /**
   * Updates the user's profile photo.
   * @param data The object containing the new profile photo data.
   * @returns {Promise<any>}
   */
  updateCurrentUserProfilePhoto(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/image', data));
  }

  /**
   * Updates the user's profile.
   * @param data The object containing the new profile data.
   * @returns {Promise<any>}
   */
  updateCurrentUserProfile(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl, data));
  }

  /**
   * Updates the user's password.
   * @param data The object containing the new password data.
   * @param jwt Optional JSON Web Token for authorization.
   * @returns {Promise<any>}
   */
  updatePassword(data, jwt?): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/password', data, { headers: { Authorization: `Bearer ${jwt}` } }));
  }

  /**
   * Sends an email with a code to verify the user's email address.
   * The code will be sent to the user's email address.
   * @param password The user's current password
   * @returns {Promise<any>}
   */
  sendEmailVerifCode(password: any): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/verif', { password }));
  }
  /**
   * change the Owner
   * @param data the data to send to change owner
   * @returns
   */
  changeOwner(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/change', data));
  }

  /**
   * Returns a QR code for application-based two-factor authentication (A2F).
   * @returns {Promise<any>}
   */
  getQrCodeA2F(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/a2f'));
  }

  /**
   * Disables application-based two-factor authentication (A2F) for the user.
   * @param {string} passwordManager The password manager used by the user.
   * @returns {Promise<any>}
   */
  disableApplicationA2F(passwordManager): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/a2f/disable', { passwordManager }));
  }

  /**
   * Enables application-based two-factor authentication (A2F) for the user.
   * @param data The data required to enable A2F
   * @returns {Promise<any>}
   */
  enableApplicationA2F(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/a2f', data));
  }

  /**
   * Returns an observable that emits when a force logout event occurs.
   * @returns {Observable<any>}
   */
  SubForceLogout(): Observable<any> {
    return this.forceLogoutUser$;
  }
}
/**
 * enum for bulk import
 * @enum {blukLoadingOptions}
 */
export enum blukLoadingOptions {
  /**
   * user
   * @type {blukLoadingOptions}
   */
  user = 'user',
  /**
   * preloadUrl
   * @type {blukLoadingOptions}
   */
  preloadUrl = 'preloadUrl',
  /**
   * preloadPassword
   * @type {blukLoadingOptions}
   */
  preloadPassword = 'preloadPassword',
  /**
   * deleteusers
   * @type {blukLoadingOptions}
   */
  deleteUsers = 'deleteUsers',
  /**
   * deletePreloadPassword
   * @type {blukLoadingOptions}
   */
  deletePreloadPassword = 'deletePreloadPassword',
  /**
   * deletePreloadUrl
   * @type {blukLoadingOptions}
   */
  deletePreloadUrl = 'deletePreloadUrl',
  /**
   * creditCard
   * @type {blukLoadingOptions}
   */
  creditCard = 'creditCard',
  /**
   * deleteCreditCard
   * @type {blukLoadingOptions}
   */
  deleteCreditCard = 'deleteCreditCard',
  /**
   * applicationAccess
   * @type {blukLoadingOptions}
   */
  applicationAccess = 'applicationAccess',
  /**
   * deleteApplicationAccess
   * @type {blukLoadingOptions}
   */
  deleteApplicationAccess = 'deleteApplicationAccess',
  /**
   * uploadUserAccessTemplate
   * @type {blukLoadingOptions}
   */
  uploadUserAccessTemplate = 'uploadUserAccessTemplate',
  /**
   * uploadUserAccessPassword
   * @type {blukLoadingOptions}
   */
  uploadUserAccessPassword = 'uploadUserAccessPassword',
  /**
   * deletePreloadedUserAcceess
   * @type {blukLoadingOptions}
   */
  deletePreloadedUserAcceess = 'deletePreloadedUserAcceess',
  /**
   * uploadDeviceRestriction
   * @type {blukLoadingOptions}
   */
  uploadDeviceRestriction = 'uploadDeviceRestriction',
  /**
   * updatePreloadPasswordImages
   * @type {blukLoadingOptions}
   */
  updatePreloadPasswordImages = 'updatePreloadPasswordImages',
  /**
   * updatePreloadUrlImages
   * @type {blukLoadingOptions}
   */
  updatePreloadUrlImages = 'updatePreloadUrlImages',
  /**
   * updateAKImages
   * @type {blukLoadingOptions}
   */
  updateAKImages = 'updateAKImages',
  /**
   * importAzureOrAd
   * @type {blukLoadingOptions}
   */
  importAzureOrAd = 'importAzureOrAd'

}
/**
 * used for bulk import
 * @type {ILoadingBulk}
 */
export interface ILoadingBulk {
  /**
   * key for loading
   * @type {blukLoadingOptions}
   */
  key: blukLoadingOptions;
  /**
   * value
   * @type {number}
   */
  value: number;
}
/**
 * used for bulk import
 * @type {TloadingBulk}
 */
export type TloadingBulk = {
  [key in blukLoadingOptions]: number;
};
/**
 * used for socket notification
 * @interface progressNotification
 */
export interface progressNotification {
  /**
   * action name
   * @type {string}
   */
  actionName:
  'ImportPP' |
  'DeletePPByTemplate' |
  'DeletePP' |
  'ImportCC' |
  'DeleteCC' |
  'ImportAK' |
  'DeleteAK' |
  'ImportUAKLogin' |
  'ImportUAKPassword' |
  'DeleteUAK' |
  'ImportPU' |
  'ImportUsers' | 'DeleteUsersByTemplate' | 'ImportUsersByAD' |
  'GroupUpdate' | 'GroupDelete' | 'GroupCreate' | 'GroupUpdate' |
  'ExportPP' | 'ExportAllUsers' | 'ExportCC' | 'ExportGRC' | 'ExportAK' | 'ExportUAK' | 'ExportPU' | 'ExportPPWithoutPassword' |
  'ImportDR' | 'ImagePU' | 'ImageAK' | 'ImagePP',
  /**
   * type
   * @type {string}
   */
  type: 'InProgress' | 'Error' | 'Success' | 'ReachingSeats',
  /**
   * number of success
   * @type {number}
   */
  success: number,
  /**
   * data
   * @type {any}
   */
  data?: any
}
